import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { AlertCircle, Loader2, MailCheck } from "lucide-react";
import TrueFlowLogo from "../components/TrueFlowLogo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

function tenantNameSafe(name) {
  return name && name.trim() ? name.trim() : "tu organización";
}

function Marco({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/">
            <TrueFlowLogo variant="light" width={440} height={120} />
          </Link>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Paso 2 de 2 del alta: el usuario llega acá desde el enlace del correo.
 *
 * Hasta el clic no existe ni el holding ni el usuario — solo un pendiente con el
 * hash del token. Ver `backend/services/registration.py` para por qué el alta se
 * partió en dos.
 */
export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const { t } = useTranslation();

  const [error, setError] = useState(null);
  const [requestSubmitted, setRequestSubmitted] = useState(null);

  // El token es de un SOLO uso: el backend lo borra al consumirlo. Así que este
  // POST no puede salir dos veces, y en desarrollo saldría, porque StrictMode
  // monta, limpia y vuelve a montar cada efecto. El segundo intento llegaría con
  // el token ya gastado y mostraría "enlace no válido" justo después de un alta
  // exitosa — el peor error posible, porque es mentira. De ahí el candado.
  const yaEnviado = useRef(false);

  useEffect(() => {
    if (!token) {
      setError(t("auth.verifyMissingToken"));
      return;
    }
    if (yaEnviado.current) return;
    yaEnviado.current = true;

    // Acá NO va el patrón `let vivo = true` + `return () => { vivo = false }`.
    // Con el candado de arriba se anulan: StrictMode limpia el primer efecto
    // —marcando `vivo = false`— y en la segunda pasada el candado corta antes de
    // pedir nada. La respuesta del único POST que sí salió llegaba y se
    // descartaba, así que la cuenta quedaba creada y la pantalla se quedaba
    // girando para siempre. Comprobado en el navegador: 200 en la red, spinner
    // eterno en pantalla.
    //
    // Escribir estado después de desmontar no rompe nada en React 18 (ya no
    // avisa ni pierde memoria), y lo que de verdad había que evitar —dos POST—
    // lo cubre el `useRef`.
    (async () => {
      try {
        const result = await verifyEmail(token);
        if (result?.kind === "request_submitted") {
          setRequestSubmitted({ tenant_name: result.tenant_name });
          return;
        }
        toast.success(t("auth.accountCreated"));
        navigate("/dashboard");
      } catch (e) {
        setError(e.response?.data?.detail || t("errors.serverError"));
      }
    })();
    // Deliberadamente sin dependencias: esto corre una vez, y el candado de
    // arriba es lo que lo garantiza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El dominio ya tenía holding: no se crea uno nuevo, queda una solicitud.
  // Recién acá se puede nombrar la empresa — quien llegó hasta este punto
  // demostró que controla una casilla de ese dominio.
  if (requestSubmitted) {
    return (
      <Marco>
        <Card className="shadow-sm" data-testid="request-submitted-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <MailCheck className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {t("auth.requestSubmittedTitle")}
            </CardTitle>
            <CardDescription className="mt-2">
              {t("auth.requestSubmittedBody", {
                tenant: tenantNameSafe(requestSubmitted.tenant_name),
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground text-center mb-6">
              {t("auth.requestSubmittedHint")}
            </p>
            <Button asChild className="w-full" variant="outline">
              <Link to="/login">{t("auth.backToLogin")}</Link>
            </Button>
          </CardContent>
        </Card>
      </Marco>
    );
  }

  if (error) {
    return (
      <Marco>
        <Card className="shadow-sm" data-testid="verify-error-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-xl">{t("auth.verifyErrorTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            {/* Reintentar es volver a registrarse: mandar el formulario otra vez
                emite un token nuevo y anula el anterior. Por eso no hay un
                endpoint de reenvío aparte. */}
            <Button asChild className="w-full">
              <Link to="/register">{t("auth.verifyErrorRetry")}</Link>
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t("auth.backToLogin")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </Marco>
    );
  }

  return (
    <Marco>
      <Card className="shadow-sm" data-testid="verify-loading-card">
        <CardContent className="pt-6 pb-6 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("auth.verifyChecking")}</p>
        </CardContent>
      </Card>
    </Marco>
  );
}
